import React, { useState, useRef, useEffect } from 'react';
import { supabase } from './supabase';

const BACKEND = 'https://tambo-production-826b.up.railway.app';

// ─── Hauler Application Form ────────────────────────────────────────────────
// Full multi-section form for haulers to apply with documents uploaded directly
// to Supabase Storage via signed URLs (file data never touches the backend).
function HaulerApplicationForm({ onBack }) {
  const [companyName, setCompanyName] = useState('');
  const [ownerFirst, setOwnerFirst] = useState('');
  const [ownerLast, setOwnerLast] = useState('');
  const [phone, setPhone] = useState('');
  const [shopAddress, setShopAddress] = useState('');
  const [yearsInBusiness, setYearsInBusiness] = useState('');
  const [dumpsterCount, setDumpsterCount] = useState('');
  const [serviceCounties, setServiceCounties] = useState('');
  const [availabilityType, setAvailabilityType] = useState('');
  const [hasGL, setHasGL] = useState(null);
  const [hasOther, setHasOther] = useState(null);
  const [coiFile, setCoiFile] = useState(null);
  const [w9File, setW9File] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showSignupPwd, setShowSignupPwd] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const shopAddrRef = useRef(null);
  const shopAcRef = useRef(null);

  useEffect(function() {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (!apiKey) return;

    function initAc() {
      if (!window.google?.maps?.places || !shopAddrRef.current || shopAcRef.current) return;
      shopAcRef.current = new window.google.maps.places.Autocomplete(
        shopAddrRef.current,
        { types: ['address'], componentRestrictions: { country: 'us' }, fields: ['formatted_address'] }
      );
      shopAcRef.current.addListener('place_changed', function() {
        const place = shopAcRef.current.getPlace();
        if (place?.formatted_address) setShopAddress(place.formatted_address);
      });
    }

    if (window.google?.maps?.places) {
      setTimeout(initAc, 50);
    } else if (!document.getElementById('gmaps-script')) {
      window.__gmapsReady = initAc;
      const s = document.createElement('script');
      s.id = 'gmaps-script';
      s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&solution_channel=GMP_QB_addressselection_v1&callback=__gmapsReady`;
      document.head.appendChild(s);
    } else {
      const timer = setInterval(function() {
        if (window.google?.maps?.places) { clearInterval(timer); initAc(); }
      }, 200);
      return function() { clearInterval(timer); };
    }
  }, []);

  const handleSubmit = async function() {
    if (!email || !password) { setMessage('Email and password are required'); return; }
    if (!companyName) { setMessage('Company name is required'); return; }
    if (!ownerFirst || !ownerLast) { setMessage('Owner first and last name are required'); return; }
    if (!phone) { setMessage('Phone number is required'); return; }
    if (hasGL === null) { setMessage('Please indicate if you have general liability insurance'); return; }

    setLoading(true);
    setMessage('');

    try {
      // Step 1: Create auth user
      const { data: authData, error: authErr } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: 'https://fasttambo.com/hauler-login',
          data: { role: 'hauler', first_name: ownerFirst, last_name: ownerLast, phone },
        },
      });
      if (authErr) throw new Error(authErr.message);
      const userId = authData.user?.id;
      if (!userId) throw new Error('Signup failed — no user ID returned');

      // Step 2: Get signed upload URLs for any documents
      let coiPath = null, w9Path = null;
      const filesToUpload = [];
      if (coiFile) filesToUpload.push({ type: 'coi', ext: coiFile.name.split('.').pop().toLowerCase() });
      if (w9File) filesToUpload.push({ type: 'w9', ext: w9File.name.split('.').pop().toLowerCase() });

      if (filesToUpload.length > 0) {
        const urlRes = await fetch(`${BACKEND}/api/auth/get-upload-urls`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, files: filesToUpload }),
        });
        if (!urlRes.ok) {
          const e = await urlRes.json();
          throw new Error('Upload URL error: ' + (e.error || 'unknown'));
        }
        const urls = await urlRes.json();

        // Step 3: Upload files directly to Supabase Storage
        if (coiFile && urls.coi) {
          coiPath = urls.coi.path;
          const r = await fetch(urls.coi.signed_url, { method: 'PUT', headers: { 'Content-Type': coiFile.type }, body: coiFile });
          if (!r.ok) throw new Error('COI upload failed');
        }
        if (w9File && urls.w9) {
          w9Path = urls.w9.path;
          const r = await fetch(urls.w9.signed_url, { method: 'PUT', headers: { 'Content-Type': w9File.type }, body: w9File });
          if (!r.ok) throw new Error('W-9 upload failed');
        }
      }

      // Step 4: Create hauler DB record with all application fields
      const signupRes = await fetch(`${BACKEND}/api/auth/hauler-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId, email,
          first_name: ownerFirst, last_name: ownerLast, phone,
          company_name: companyName,
          owner_first_name: ownerFirst, owner_last_name: ownerLast,
          shop_address: shopAddress || null,
          years_in_business: yearsInBusiness || null,
          has_general_liability: hasGL,
          has_other_insurance: hasOther,
          coi_url: coiPath, w9_url: w9Path,
          dumpster_count: dumpsterCount || null,
          service_counties: serviceCounties || null,
          availability_type: availabilityType || null,
          sms_consent: smsConsent,
          sms_consent_at: smsConsent ? new Date().toISOString() : null,
        }),
      });
      if (!signupRes.ok) {
        const e = await signupRes.json();
        throw new Error(e.error || 'Application submission failed');
      }

      setSubmitted(true);
    } catch (err) {
      setMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={{background:'#FAFAF8',minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'32px 24px',textAlign:'center',fontFamily:'sans-serif'}}>
        <div style={{fontSize:'26px',fontWeight:'800',color:'#0D0D0D',marginBottom:'32px'}}>Tam<span style={{color:'#2EA865'}}>bo</span></div>
        <div style={{width:'72px',height:'72px',background:'#E8F5EE',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'36px',marginBottom:'20px'}}>✅</div>
        <div style={{fontSize:'22px',fontWeight:'800',color:'#0D0D0D',marginBottom:'12px'}}>Application Submitted!</div>
        <div style={{fontSize:'15px',color:'#374151',marginBottom:'8px',fontWeight:'600'}}>Thanks for applying to haul with Tambo!</div>
        <div style={{fontSize:'14px',color:'#6B6B6B',maxWidth:'320px',lineHeight:'1.7',marginBottom:'8px'}}>
          We'll review your application and reach out within 1-2 business days.
        </div>
        <div style={{fontSize:'14px',color:'#6B6B6B',maxWidth:'320px',lineHeight:'1.7',marginBottom:'36px'}}>
          Questions? Call us at <a href="tel:+19562753688" style={{color:'#1A7A4A',fontWeight:'600',textDecoration:'none'}}>+1 (956) 275-3688</a>
        </div>
        <a href="/hauler-login" style={{background:'#1A7A4A',color:'white',padding:'12px 28px',borderRadius:'10px',fontSize:'14px',fontWeight:'700',textDecoration:'none'}}>
          Sign In
        </a>
      </div>
    );
  }

  const F = {
    field: { marginBottom: 16 },
    label: { display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' },
    input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #E0E0DA', fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
    row: { display: 'flex', gap: 12 },
    section: { fontSize: 12, fontWeight: 800, color: '#1A7A4A', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14, marginTop: 24, paddingBottom: 7, borderBottom: '2px solid #E8F5EE' },
    yesNo: { display: 'flex', gap: 10 },
    fileInput: { display: 'none' },
    fileLabel: (set) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: `1.5px dashed ${set ? '#1A7A4A' : '#D1D5DB'}`, background: set ? '#E8F5EE' : '#FAFAFA', cursor: 'pointer', fontSize: 13, color: set ? '#1A7A4A' : '#6B6B6B', fontWeight: set ? 600 : 500 }),
    yesNoBtn: (active) => ({ flex: 1, padding: '9px 0', borderRadius: 8, border: `1.5px solid ${active ? '#1A7A4A' : '#E0E0DA'}`, background: active ? '#E8F5EE' : 'white', color: active ? '#1A7A4A' : '#6B6B6B', fontWeight: active ? 700 : 500, fontSize: 14, cursor: 'pointer' }),
  };

  return (
    <div style={{ background: '#FAFAF8', minHeight: '100vh', fontFamily: 'sans-serif' }}>
      <div style={{ background: '#0D0D0D', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'white', opacity: 0.7, cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px 0 0' }}>←</button>
        <div onClick={function(){ window.location.href = '/'; }} style={{ fontSize: 22, fontWeight: 800, color: 'white', cursor: 'pointer' }}>Tambo</div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginLeft: 4 }}>Hauler Application</div>
      </div>

      <div style={{ maxWidth: 540, margin: '0 auto', padding: '24px 20px 60px' }}>
        <div style={{ background: '#E8F5EE', borderRadius: 10, padding: '12px 16px', marginBottom: 8, fontSize: 13, color: '#1A7A4A', lineHeight: 1.5 }}>
          Applications are reviewed within 1-2 business days. You'll be notified by phone and email once approved.
        </div>

        {/* Business Info */}
        <div style={F.section}>Business Info</div>
        <div style={F.field}>
          <label style={F.label}>Company Name *</label>
          <input style={F.input} placeholder="Acme Hauling LLC" value={companyName} onChange={e => setCompanyName(e.target.value)} />
        </div>
        <div style={{ ...F.row, marginBottom: 0 }}>
          <div style={{ ...F.field, flex: 1 }}>
            <label style={F.label}>Owner First Name *</label>
            <input style={F.input} placeholder="John" value={ownerFirst} onChange={e => setOwnerFirst(e.target.value)} />
          </div>
          <div style={{ ...F.field, flex: 1 }}>
            <label style={F.label}>Owner Last Name *</label>
            <input style={F.input} placeholder="Smith" value={ownerLast} onChange={e => setOwnerLast(e.target.value)} />
          </div>
        </div>
        <div style={F.field}>
          <label style={F.label}>Cell Phone *</label>
          <input style={F.input} type="tel" placeholder="(956) 555-0100" value={phone} onChange={e => setPhone(e.target.value)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, padding: '10px 12px', background: '#f8f8f6', borderRadius: 8, border: '1px solid #e0e0da' }}>
          <input
            id="hauler-sms-consent"
            type="checkbox"
            checked={smsConsent}
            onChange={function(e) { setSmsConsent(e.target.checked); }}
            style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, accentColor: '#1A7A4A', cursor: 'pointer' }}
          />
          <label htmlFor="hauler-sms-consent" style={{ fontSize: 12, color: '#374151', lineHeight: 1.5, cursor: 'pointer' }}>
            I agree to receive SMS text messages from Tambo about my order (confirmations, delivery/pickup updates) and account. Message and data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help. See our{' '}
            <a href="/privacy" style={{ color: '#1A7A4A', textDecoration: 'underline' }}>Privacy Policy</a>
            {' '}and{' '}
            <a href="/terms" style={{ color: '#1A7A4A', textDecoration: 'underline' }}>Terms of Service</a>.
          </label>
        </div>

        {/* Location */}
        <div style={F.section}>Location</div>
        <div style={F.field}>
          <label style={F.label}>Shop / Yard Address</label>
          <input ref={shopAddrRef} style={F.input} placeholder="123 Industrial Blvd, Edinburg, TX" value={shopAddress} onChange={e => setShopAddress(e.target.value)} />
        </div>
        <div style={F.field}>
          <label style={F.label}>Years in Business</label>
          <input style={F.input} type="number" min="0" placeholder="3" value={yearsInBusiness} onChange={e => setYearsInBusiness(e.target.value)} />
        </div>

        {/* Fleet */}
        <div style={F.section}>Fleet & Operations</div>
        <div style={F.field}>
          <label style={F.label}>Number of Dumpsters</label>
          <input style={F.input} type="number" min="1" placeholder="5" value={dumpsterCount} onChange={e => setDumpsterCount(e.target.value)} />
        </div>
        <div style={F.field}>
          <label style={F.label}>Counties Served</label>
          <input style={F.input} placeholder="Hidalgo, Cameron, Willacy" value={serviceCounties} onChange={e => setServiceCounties(e.target.value)} />
        </div>
        <div style={F.field}>
          <label style={F.label}>Availability</label>
          <select style={F.input} value={availabilityType} onChange={e => setAvailabilityType(e.target.value)}>
            <option value="">Select availability</option>
            <option value="full_time">Full-time (Mon–Sat)</option>
            <option value="part_time">Part-time</option>
            <option value="weekends">Weekends only</option>
          </select>
        </div>

        {/* Insurance */}
        <div style={F.section}>Insurance</div>
        <div style={F.field}>
          <label style={F.label}>Do you have General Liability insurance? *</label>
          <div style={F.yesNo}>
            <button style={F.yesNoBtn(hasGL === true)} onClick={() => setHasGL(true)}>Yes</button>
            <button style={F.yesNoBtn(hasGL === false)} onClick={() => setHasGL(false)}>No</button>
          </div>
        </div>
        <div style={F.field}>
          <label style={F.label}>Other commercial insurance?</label>
          <div style={F.yesNo}>
            <button style={F.yesNoBtn(hasOther === true)} onClick={() => setHasOther(true)}>Yes</button>
            <button style={F.yesNoBtn(hasOther === false)} onClick={() => setHasOther(false)}>No</button>
          </div>
        </div>

        {/* Documents */}
        <div style={F.section}>Documents</div>
        <div style={F.field}>
          <label style={F.label}>Certificate of Insurance (COI)</label>
          <input type="file" id="coi-file" accept=".pdf,.jpg,.jpeg,.png" style={F.fileInput} onChange={e => setCoiFile(e.target.files[0] || null)} />
          <label htmlFor="coi-file" style={F.fileLabel(!!coiFile)}>
            <span style={{ fontSize: 16 }}>{coiFile ? '✓' : '📎'}</span>
            <span>{coiFile ? coiFile.name : 'Upload COI (PDF, JPG, PNG)'}</span>
          </label>
        </div>
        <div style={F.field}>
          <label style={F.label}>W-9 Form</label>
          <input type="file" id="w9-file" accept=".pdf,.jpg,.jpeg,.png" style={F.fileInput} onChange={e => setW9File(e.target.files[0] || null)} />
          <label htmlFor="w9-file" style={F.fileLabel(!!w9File)}>
            <span style={{ fontSize: 16 }}>{w9File ? '✓' : '📎'}</span>
            <span>{w9File ? w9File.name : 'Upload W-9 (PDF, JPG, PNG)'}</span>
          </label>
        </div>

        {/* Login Credentials */}
        <div style={F.section}>Login Credentials</div>
        <div style={F.field}>
          <label style={F.label}>Email Address *</label>
          <input style={F.input} type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} />
        </div>
        <div style={F.field}>
          <label style={F.label}>Password *</label>
          <div style={{ position: 'relative' }}>
            <input style={{ ...F.input, paddingRight: 40 }} type={showSignupPwd ? 'text' : 'password'} placeholder="Min 6 characters" value={password} onChange={e => setPassword(e.target.value)} />
            <button type="button" onClick={function() { setShowSignupPwd(function(v) { return !v; }); }} tabIndex={-1} aria-label={showSignupPwd ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#9ca3af', lineHeight: 0 }}>
              {showSignupPwd
                ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>
        </div>

        {message && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: '#DC2626', fontWeight: 500 }}>
            {message}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{ width: '100%', padding: '14px', background: loading ? '#9CA3AF' : '#1A7A4A', color: 'white', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', marginTop: 4 }}
        >
          {loading ? 'Submitting Application...' : 'Submit Application'}
        </button>

        <p style={{ textAlign: 'center', fontSize: 13, color: '#9CA3AF', marginTop: 16 }}>
          Already have an account?{' '}
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#1A7A4A', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: 13 }}>Sign in</button>
        </p>
      </div>
    </div>
  );
}

function Auth({ onLogin, role, defaultPage }) {
  const [page, setPage] = useState(defaultPage || 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showAuthPwd, setShowAuthPwd] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const billingAddrRef = useRef(null);
  const billingAcRef = useRef(null);

  useEffect(function() {
    if (role !== 'client' || page !== 'signup') return;
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (!apiKey) return;

    function initBillingAc() {
      if (!window.google?.maps?.places || !billingAddrRef.current || billingAcRef.current) return;
      billingAcRef.current = new window.google.maps.places.Autocomplete(
        billingAddrRef.current,
        { types: ['address'], componentRestrictions: { country: 'us' }, fields: ['formatted_address'] }
      );
      billingAcRef.current.addListener('place_changed', function() {
        const place = billingAcRef.current.getPlace();
        if (place && place.formatted_address) setBillingAddress(place.formatted_address);
      });
    }

    if (window.google?.maps?.places) {
      setTimeout(initBillingAc, 50);
      return;
    }
    if (!document.getElementById('gmaps-script')) {
      window.__gmapsReady = initBillingAc;
      const s = document.createElement('script');
      s.id = 'gmaps-script';
      s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&solution_channel=GMP_QB_addressselection_v1&callback=__gmapsReady`;
      document.head.appendChild(s);
    } else {
      const timer = setInterval(function() {
        if (window.google?.maps?.places) { clearInterval(timer); initBillingAc(); }
      }, 200);
      return function() { clearInterval(timer); };
    }
  }, [role, page]);

  const handleLogin = async function() {
    if (!email || !password) { setMessage('Please enter your email and password'); return; }
    setLoading(true);
    setMessage('');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setMessage(error.message);
      setLoading(false);
      return;
    }
    if (!data.session) {
      setMessage('Login succeeded but no session was established. Please try again.');
      setLoading(false);
      return;
    }
    // Brief delay to ensure session is flushed to localStorage before navigation
    await new Promise(function(resolve) { setTimeout(resolve, 500); });
    // When on the client login page, check whether this user is actually a hauler
    // so they get routed to the right dashboard instead of the client pending screen.
    let resolvedRole = role;
    if (role === 'client' && data.user) {
      try {
        const res = await fetch(`${BACKEND}/api/auth/role?user_id=${data.user.id}`);
        if (res.ok) {
          const rd = await res.json();
          if (rd.role) resolvedRole = rd.role;
        }
      } catch (_) { /* fall back to 'client' */ }
    }
    onLogin(data.user, resolvedRole);
    setLoading(false);
  };

  const handleSignup = async function() {
    if (!email || !password) { setMessage('Please enter your email and password'); return; }
    if (!firstName || !lastName || !phone) { setMessage('Please fill in your name and phone number'); return; }
    if (role === 'client' && !billingAddress) { setMessage('Please enter your billing address'); return; }
    setLoading(true);
    setMessage('');
    const metadata = role === 'hauler'
      ? { role: 'hauler', first_name: firstName, last_name: lastName, phone }
      : { role: 'client', first_name: firstName, last_name: lastName, phone, company_name: companyName, billing_address: billingAddress, sms_consent: smsConsent };
    const result = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: role === 'hauler' ? 'https://fasttambo.com/hauler-login' : 'https://fasttambo.com/login',
        data: metadata,
      }
    });
    if (result.error) {
      setMessage(result.error.message);
      setLoading(false);
    } else {
      // Explicitly create DB rows for hauler signups so they appear in admin queue.
      // This runs regardless of whether the Supabase trigger is installed.
      if (role === 'hauler' && result.data?.user?.id) {
        try {
          await fetch(`${BACKEND}/api/auth/hauler-signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: result.data.user.id,
              email,
              first_name: firstName,
              last_name: lastName,
              phone,
            }),
          });
        } catch (e) {
          console.error('[hauler-signup] Failed to create DB rows:', e);
        }
      }
      if (role === 'client' && result.data?.user?.id) {
        try {
          await fetch(`${BACKEND}/api/auth/client-signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: result.data.user.id,
              email,
              first_name: firstName,
              last_name: lastName,
              phone,
              sms_consent: smsConsent,
            }),
          });
        } catch (e) {
          console.error('[client-signup] Failed to create DB rows:', e);
        }
      }
      setSubmitted(true);
      setLoading(false);
    }
  };

  const handleForgot = async function() {
    if (!email) { setMessage('Please enter your email address'); return; }
    setLoading(true);
    setMessage('');
    await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: 'https://fasttambo.com/reset-password?role=' + role,
    });
    setLoading(false);
    setForgotSent(true);
  };

  // Hauler signup uses the comprehensive application form instead of the simple form
  if (role === 'hauler' && page === 'signup') {
    return <HaulerApplicationForm onBack={function() { setPage('login'); }} />;
  }

  if (submitted && role === 'hauler') {
    return (
      <div style={{background:'#FAFAF8',minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'32px 24px',textAlign:'center',fontFamily:'sans-serif'}}>
        <div style={{fontSize:'26px',fontWeight:'800',color:'#0D0D0D',marginBottom:'32px'}}>
          Tam<span style={{color:'#2EA865'}}>bo</span>
        </div>
        <div style={{width:'72px',height:'72px',background:'#E8F5EE',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'36px',marginBottom:'20px'}}>✅</div>
        <div style={{fontSize:'22px',fontWeight:'800',color:'#0D0D0D',marginBottom:'12px'}}>Application Submitted!</div>
        <div style={{fontSize:'15px',color:'#374151',marginBottom:'8px',fontWeight:'600'}}>Thanks for signing up!</div>
        <div style={{fontSize:'14px',color:'#6B6B6B',maxWidth:'300px',lineHeight:'1.7',marginBottom:'8px'}}>
          Your application is under review. You'll receive an email once you're approved.
        </div>
        <div style={{fontSize:'14px',color:'#6B6B6B',maxWidth:'300px',lineHeight:'1.7',marginBottom:'36px'}}>
          Questions? Call us at <a href="tel:+19562753688" style={{color:'#1A7A4A',fontWeight:'600',textDecoration:'none'}}>+1 (956) 275-3688</a>
        </div>
        <a href="/hauler-login" style={{background:'#1A7A4A',color:'white',padding:'12px 28px',borderRadius:'10px',fontSize:'14px',fontWeight:'700',textDecoration:'none'}}>
          Sign In
        </a>
      </div>
    );
  }

  if (submitted && role === 'client') {
    return (
      <div style={{background:'#FAFAF8',minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'32px 24px',textAlign:'center',fontFamily:'sans-serif'}}>
        <div style={{fontSize:'26px',fontWeight:'800',color:'#0D0D0D',marginBottom:'32px'}}>
          Tam<span style={{color:'#2EA865'}}>bo</span>
        </div>
        <div style={{width:'72px',height:'72px',background:'#E8F5EE',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'36px',marginBottom:'20px'}}>✅</div>
        <div style={{fontSize:'22px',fontWeight:'800',color:'#0D0D0D',marginBottom:'12px'}}>Application Submitted!</div>
        <div style={{fontSize:'15px',color:'#374151',marginBottom:'8px',fontWeight:'600'}}>Thanks for signing up!</div>
        <div style={{fontSize:'14px',color:'#6B6B6B',maxWidth:'300px',lineHeight:'1.7',marginBottom:'8px'}}>
          Your account is under review. You'll be notified once approved.
        </div>
        <div style={{fontSize:'14px',color:'#6B6B6B',maxWidth:'300px',lineHeight:'1.7',marginBottom:'36px'}}>
          Questions? Call <a href="tel:+19562263903" style={{color:'#1A7A4A',fontWeight:'600',textDecoration:'none'}}>(956) 226-3903</a>
        </div>
        <a href="/login" style={{background:'#1A7A4A',color:'white',padding:'12px 28px',borderRadius:'10px',fontSize:'14px',fontWeight:'700',textDecoration:'none'}}>
          Sign In
        </a>
      </div>
    );
  }

  if (page === 'forgot') {
    return (
      <div className="auth-wrap">
        <div className="auth-hero">
          <div className="auth-logo" onClick={function(){window.location.href='/';}} style={{cursor:'pointer'}}>Tambo</div>
          <p>{role === 'hauler' ? 'Hauler Portal' : 'Client Dashboard Login'}</p>
        </div>
        <div className="auth-body">
          {forgotSent ? (
            <>
              <h2>Check your email</h2>
              <p style={{fontSize:14,color:'#6b7280',lineHeight:1.6,marginBottom:24}}>
                If an account exists for that email, a reset link has been sent. Check your inbox and spam folder.
              </p>
              <button className="auth-btn" onClick={function() { setPage('login'); setForgotSent(false); setMessage(''); }}>
                ← Back to sign in
              </button>
            </>
          ) : (
            <>
              <h2>Reset your password</h2>
              <p style={{fontSize:14,color:'#6b7280',marginBottom:20}}>Enter your email and we'll send you a reset link.</p>
              <div className="auth-field">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={function(e) { setEmail(e.target.value); setMessage(''); }}
                  onKeyDown={function(e) { if (e.key === 'Enter') handleForgot(); }}
                />
              </div>
              {message && <div className="auth-message">{message}</div>}
              <button className="auth-btn" onClick={handleForgot} disabled={loading}>
                {loading ? 'Sending…' : 'Send Reset Email'}
              </button>
              <div className="auth-footer">
                <p><span onClick={function() { setPage('login'); setMessage(''); }}>← Back to sign in</span></p>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="auth-wrap">
      <div className="auth-hero">
        <div className="auth-logo" onClick={function(){window.location.href='/';}} style={{cursor:'pointer'}}>Tambo</div>
        <p>{role === 'hauler' ? 'Hauler Portal' : 'Client Dashboard Login'}</p>
      </div>
      <div className="auth-body">
        <div className="auth-tabs">
          <button
            className={page === 'login' ? 'active' : ''}
            onClick={function() { setPage('login'); setMessage(''); }}
          >
            Sign In
          </button>
          <button
            className={page === 'signup' ? 'active' : ''}
            onClick={function() { setPage('signup'); setMessage(''); }}
          >
            Sign Up
          </button>
        </div>

        <h2>
          {page === 'login' ? 'Welcome back' : role === 'hauler' ? 'Join as a Hauler' : 'Create Client Account'}
        </h2>

        {role === 'hauler' && page === 'signup' && (
          <div className="auth-hauler-note">
            🚛 Hauler accounts require approval before you can receive jobs.
          </div>
        )}
        {role === 'client' && page === 'signup' && (
          <div className="auth-hauler-note">
            📋 Client accounts require approval. We'll notify you once your account is active.
          </div>
        )}

        {page === 'signup' && (
          <>
            <div style={{display:'flex',gap:'12px'}}>
              <div className="auth-field" style={{flex:1}}>
                <label>First Name</label>
                <input
                  type="text"
                  placeholder="John"
                  value={firstName}
                  onChange={function(e) { setFirstName(e.target.value); }}
                />
              </div>
              <div className="auth-field" style={{flex:1}}>
                <label>Last Name</label>
                <input
                  type="text"
                  placeholder="Smith"
                  value={lastName}
                  onChange={function(e) { setLastName(e.target.value); }}
                />
              </div>
            </div>
            <div className="auth-field">
              <label>Cell Phone Number</label>
              <input
                type="tel"
                placeholder="(555) 555-5555"
                value={phone}
                onChange={function(e) { setPhone(e.target.value); }}
              />
            </div>
          </>
        )}

        {role === 'client' && page === 'signup' && (
          <>
            <div className="auth-field">
              <label>Company Name <span style={{fontWeight:400,textTransform:'none',letterSpacing:0,color:'#9B9B9B'}}>(optional)</span></label>
              <input
                type="text"
                placeholder="Acme Corp"
                value={companyName}
                onChange={function(e) { setCompanyName(e.target.value); }}
              />
            </div>
            <div className="auth-field">
              <label>Billing Address</label>
              <input
                ref={billingAddrRef}
                type="text"
                placeholder="123 Main St, City, TX"
                value={billingAddress}
                onChange={function(e) { setBillingAddress(e.target.value); }}
              />
            </div>
            <div style={{display:'flex',alignItems:'flex-start',gap:'10px',marginBottom:'4px',padding:'10px 12px',background:'#f8f8f6',borderRadius:'8px',border:'1px solid #e0e0da'}}>
              <input
                id="client-sms-consent"
                type="checkbox"
                checked={smsConsent}
                onChange={function(e) { setSmsConsent(e.target.checked); }}
                style={{marginTop:'2px',width:'16px',height:'16px',flexShrink:0,accentColor:'#1A7A4A',cursor:'pointer'}}
              />
              <label htmlFor="client-sms-consent" style={{fontSize:'12px',color:'#374151',lineHeight:'1.5',cursor:'pointer'}}>
                I agree to receive SMS text messages from Tambo about my order (confirmations, delivery/pickup updates) and account. Message and data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help. See our{' '}
                <a href="/privacy" style={{color:'#1A7A4A',textDecoration:'underline'}}>Privacy Policy</a>
                {' '}and{' '}
                <a href="/terms" style={{color:'#1A7A4A',textDecoration:'underline'}}>Terms of Service</a>.
              </label>
            </div>
          </>
        )}

        <div className="auth-field">
          <label>Email</label>
          <input
            type="email"
            placeholder="you@email.com"
            value={email}
            onChange={function(e) { setEmail(e.target.value); }}
          />
        </div>

        <div className="auth-field">
          <label>Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showAuthPwd ? 'text' : 'password'}
              placeholder="Your password"
              value={password}
              onChange={function(e) { setPassword(e.target.value); }}
              style={{ paddingRight: 40, boxSizing: 'border-box', width: '100%' }}
            />
            <button type="button" onClick={function() { setShowAuthPwd(function(v) { return !v; }); }} tabIndex={-1} aria-label={showAuthPwd ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#9ca3af', lineHeight: 0 }}>
              {showAuthPwd
                ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>
        </div>

        {message && (
          <div className="auth-message">{message}</div>
        )}

        {page === 'login' ? (
          <button className="auth-btn" onClick={handleLogin} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        ) : (
          <button className="auth-btn" onClick={handleSignup} disabled={loading}>
            {loading ? 'Creating account...' : role === 'hauler' ? 'Apply as Hauler' : 'Sign Up as Client'}
          </button>
        )}

        {page === 'login' && (
          <div style={{textAlign:'center',marginTop:10}}>
            <span onClick={function(){setPage('forgot');setMessage('');setForgotSent(false);}} style={{fontSize:12,color:'#9ca3af',cursor:'pointer',textDecoration:'underline'}}>
              Forgot password?
            </span>
          </div>
        )}

        <div className="auth-footer">
          {page === 'login' ? (
            <p>New to Tambo? <span onClick={function() { setPage('signup'); }}>Sign up free</span></p>
          ) : (
            <p>Already have an account? <span onClick={function() { setPage('login'); }}>Sign in</span></p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Auth;
